import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatINR, formatDate } from "@/lib/constants";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const COLUMNS = ["Pending", "In Progress", "Submitted", "Confirmed"];

export default function ExecutionQueue() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/execution-tasks");
      setTasks(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const move = async (t, next) => {
    try {
      await api.patch(`/execution-tasks/${t.id}`, { status: next });
      toast.success(`Moved to ${next}`);
      load();
    } catch (err) { toast.error("Failed"); }
  };

  const groups = Object.fromEntries(COLUMNS.map((c) => [c, tasks.filter((t) => t.status === c)]));

  return (
    <div className="space-y-6">
      <div>
        <p className="overline mb-1 text-muted-foreground">Stage 4 · Execution</p>
        <h1 className="font-dashboard text-3xl font-bold tracking-tight">Execution queue</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Instruments set up per approved plan. When all tasks confirm, the client moves into Ongoing Review.
        </p>
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground">Loading…</p>
      ) : tasks.length === 0 ? (
        <Card className="border-border bg-white p-10 text-center text-muted-foreground">
          Nothing here yet. Tasks appear once clients approve their plans.
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-4">
          {COLUMNS.map((col, idx) => (
            <div key={col} className="rounded-lg border border-border bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="font-dashboard text-sm font-bold">{col}</div>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-xs" data-testid={`queue-count-${col.replace(/\s+/g, "-").toLowerCase()}`}>{groups[col].length}</span>
              </div>
              <div className="space-y-2">
                {groups[col].map((t) => (
                  <div key={t.id} className="rounded-md border border-border bg-secondary/30 p-3 text-sm" data-testid={`queue-task-${t.id}`}>
                    <div className="font-medium">{t.category}</div>
                    {t.fund_name && <div className="text-xs font-semibold text-primary">{t.fund_name}</div>}
                    <div className="text-xs text-muted-foreground">{t.mf_sub_category || t.instrument_type}</div>
                    <div className="mt-1 text-xs">{formatINR(t.target_amount)}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">Created {formatDate(t.created_at)}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(`/app/clients/${t.client_id}`)} data-testid={`queue-open-${t.id}`}>Open client</Button>
                      {idx < COLUMNS.length - 1 && (
                        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={() => move(t, COLUMNS[idx + 1])} data-testid={`queue-advance-${t.id}`}>
                          {COLUMNS[idx + 1]} <ArrowRight className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    {t.status === "Confirmed" && <CheckCircle2 className="mt-2 h-4 w-4 text-primary" />}
                  </div>
                ))}
                {groups[col].length === 0 && <p className="text-xs text-muted-foreground">Empty</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
